import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Grid,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SiteCodeSelector from './components/SiteCodeSelector.component';
import SiteCodesDisplay from './components/SiteCodesDisplay.component';
import { useFiberDashboard } from './context/fiberDashboardContext';

const EnhancedBulkEditModal = ({
  open,
  onClose,
  selectedSites = [],
  onUpdate,
  isLoading,
  dashboardData = [],
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFields, setSelectedFields] = useState([]);
  const [fieldValues, setFieldValues] = useState({});
  const [siteCodes, setSiteCodes] = useState([]);
  const [useSiteCodeSelector, setUseSiteCodeSelector] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);
  const loading = isLoading !== undefined ? isLoading : localLoading;
  const { refreshDashboardData } = useFiberDashboard();

  // Define all available fiber fields
  const allFields = [
    { key: 'site_code', label: 'Site Code', type: 'text', disabled: true },
    { key: 'project_name', label: 'Project Name', type: 'text' },
    { key: 'onfp_planning_remarks', label: 'ONFP Planning Remarks', type: 'text' },
    { key: 'bp_remarks', label: 'BP Remarks', type: 'text' },
    { key: 'comments', label: 'Comments', type: 'text' },
    { key: 'phase_name', label: 'Phase Name', type: 'text' },
    { key: 'bp', label: 'BP', type: 'text' },
    { key: 'fiber_site_name', label: 'Fiber Site Name', type: 'text' },
    { key: 'lat', label: 'Latitude', type: 'number' },
    { key: 'long', label: 'Longitude', type: 'number' },
    { key: 'technical_city', label: 'Technical City', type: 'text' },
    { key: 'region', label: 'Region', type: 'text' },
    { key: 'vendor', label: 'Vendor', type: 'text' },
    { key: 'fiber_pop_id', label: 'Fiber POP ID', type: 'text' },
    { key: 'ring_name', label: 'Ring Name', type: 'text' },
    { key: 'ring_unique_name', label: 'Ring Unique Name', type: 'text' },
    { key: 'ring_name_nms', label: 'Ring Name NMS', type: 'text' },
    { key: 'cutover_date', label: 'Cutover Date', type: 'date' },
    { key: 'total_length', label: 'Total Length', type: 'number' },
    { key: 'on_net', label: 'On Net', type: 'text' },
    { key: 'off_net', label: 'Off Net', type: 'text' },
    { key: 'prefix', label: 'Prefix', type: 'text' },
    { key: 'old_site_code', label: 'Old Site Code', type: 'text' },
    { key: 'replacement_site_code', label: 'Replacement Site Code', type: 'text' },
    { key: 'replacement_site_name', label: 'Replacement Site Name', type: 'text' },
    { key: 'replacement_site_lat', label: 'Replacement Site Latitude', type: 'number' },
    { key: 'replacement_site_long', label: 'Replacement Site Longitude', type: 'number' },
    {
      key: 'replacement_site_proposed_ring',
      label: 'Replacement Site Proposed Ring',
      type: 'text',
    },
    { key: 'addendum_status', label: 'Addendum Status', type: 'text' },
    { key: 'descope_status', label: 'Descope Status', type: 'text' },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: ['On-Air', 'In-Progress', 'Planned', 'Non-Fiber', 'Descoped'],
    },
    {
      key: 'is_ftts',
      label: 'Is FTTS',
      type: 'select',
      options: ['Yes', 'No'],
    },
  ];

  const steps = ['Select Sites', 'Select Fields', 'Edit Values'];

  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setSelectedFields([]);
      setFieldValues({});
      setSiteCodes([]);
      setUseSiteCodeSelector(false);
    }
  }, [open]);

  // Initialize with selected sites if provided
  useEffect(() => {
    if (selectedSites && selectedSites.length > 0 && !useSiteCodeSelector) {
      setSiteCodes(selectedSites);
    }
  }, [selectedSites, useSiteCodeSelector]);

  const handleFieldSelection = (fieldKey, checked) => {
    if (checked) {
      setSelectedFields([...selectedFields, fieldKey]);
      // Initialize field value based on first selected site
      if (siteCodes && siteCodes.length > 0) {
        const firstSite = siteCodes[0];
        setFieldValues((prev) => ({
          ...prev,
          [fieldKey]: firstSite[fieldKey] || '',
        }));
      }
    } else {
      setSelectedFields(selectedFields.filter((field) => field !== fieldKey));
      const newFieldValues = { ...fieldValues };
      delete newFieldValues[fieldKey];
      setFieldValues(newFieldValues);
    }
  };

  const handleFieldValueChange = (fieldKey, value) => {
    setFieldValues((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  const handleNext = () => {
    if (activeStep === 0) {
      // Validate site selection
      if (siteCodes.length === 0) {
        alert('Please select at least one site to edit');
        return;
      }
    } else if (activeStep === 1) {
      // Validate field selection
      if (selectedFields.length === 0) {
        alert('Please select at least one field to edit');
        return;
      }
    }
    setActiveStep(activeStep + 1);
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const handleSubmit = async () => {
    if (selectedFields.length === 0) {
      alert('Please select at least one field to edit');
      return;
    }

    if (isLoading === undefined) {
      setLocalLoading(true);
    }
    try {
      // Prepare updated sites data
      const updatedSites = siteCodes.map((site) => {
        const updatedSite = { ...site };
        selectedFields.forEach((fieldKey) => {
          if (fieldValues[fieldKey] !== undefined) {
            updatedSite[fieldKey] = fieldValues[fieldKey];
          }
        });
        // Ensure off_net is always included if it exists in the site object
        if (site.hasOwnProperty('off_net')) {
          updatedSite.off_net =
            site.off_net !== undefined && site.off_net !== null ? site.off_net : '';
        }
        return updatedSite;
      });

      // Call the update function
      await onUpdate(updatedSites);

      // Refresh dashboard data to get updated information
      try {
        await refreshDashboardData();
        console.log('Dashboard data refreshed after enhanced bulk edit');

        // Also refresh the detailed table data to update count
        window.dispatchEvent(new CustomEvent('refreshFiberTableData'));
      } catch (error) {
        console.error('Error refreshing dashboard data:', error);
        // Don't show error to user as the main operation was successful
      }

      // Close modal and reset
      onClose();
      setActiveStep(0);
      setSelectedFields([]);
      setFieldValues({});
      setSiteCodes([]);
    } catch (error) {
      console.error('Error updating sites:', error);
      alert('Error updating sites. Please try again.');
    } finally {
      if (isLoading === undefined) {
        setLocalLoading(false);
      }
    }
  };

  const renderFieldInput = (field) => {
    const value = fieldValues[field.key] || '';

    switch (field.type) {
      case 'select':
        return (
          <FormControl fullWidth size="small">
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={value}
              onChange={(e) => handleFieldValueChange(field.key, e.target.value)}
              label={field.label}
            >
              {field.options.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'date':
        return (
          <TextField
            fullWidth
            size="small"
            type="date"
            value={value}
            onChange={(e) => handleFieldValueChange(field.key, e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        );
      case 'number':
        return (
          <TextField
            fullWidth
            size="small"
            type="number"
            value={value}
            onChange={(e) => handleFieldValueChange(field.key, e.target.value)}
          />
        );
      default:
        return (
          <TextField
            fullWidth
            size="small"
            value={value}
            onChange={(e) => handleFieldValueChange(field.key, e.target.value)}
            disabled={field.disabled}
          />
        );
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: '#667085', mb: 2 }}>
              Choose how you want to select sites for bulk editing:
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={!useSiteCodeSelector}
                  onChange={(e) => setUseSiteCodeSelector(!e.target.checked)}
                />
              }
              label="Use currently selected sites from table"
              sx={{ mb: 2 }}
            />

            {!useSiteCodeSelector && selectedSites.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Using {selectedSites.length} site(s) selected from the table
              </Alert>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={useSiteCodeSelector}
                  onChange={(e) => setUseSiteCodeSelector(e.target.checked)}
                />
              }
              label="Search and select specific site codes"
              sx={{ mb: 2 }}
            />

            {useSiteCodeSelector && (
              <Box sx={{ mt: 2 }}>
                <SiteCodeSelector
                  selectedSiteCodes={siteCodes}
                  onSiteCodesChange={setSiteCodes}
                  maxSelections={1000} // Allow up to 1000 sites
                  dashboardData={dashboardData}
                />
              </Box>
            )}

            {siteCodes.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Selected Sites ({siteCodes.length})
                </Typography>
                <SiteCodesDisplay selectedSites={siteCodes} maxVisible={5} showCount={true} />
              </Box>
            )}
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: '#667085', mb: 2 }}>
              Select the fields you want to edit for all selected sites:
            </Typography>
            <Grid container spacing={2}>
              {allFields.map((field) => (
                <Grid item xs={12} sm={4} key={field.key}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedFields.includes(field.key)}
                        disabled={field.key === 'site_code'}
                        onChange={(e) => handleFieldSelection(field.key, e.target.checked)}
                        sx={{
                          color: '#D0D5DD',
                          '&.Mui-checked': {
                            color: '#BD1D23',
                          },
                          '&.Mui-disabled': {
                            color: '#D0D5DD',
                          },
                        }}
                      />
                    }
                    label={field.label}
                    sx={{
                      opacity: field.key === 'site_code' ? 0.6 : 1,
                      '& .MuiFormControlLabel-label': {
                        fontSize: '14px',
                        color: field.key === 'site_code' ? '#9E9E9E' : '#344054',
                        fontWeight: 500,
                      },
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: '#667085', mb: 2 }}>
              Enter new values for the selected fields. These values will be applied to all selected
              sites:
            </Typography>
            <Grid container spacing={2}>
              {selectedFields.map((fieldKey) => {
                const field = allFields.find((f) => f.key === fieldKey);
                return (
                  <Grid item xs={12} sm={6} key={fieldKey}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ color: '#344054', mb: 1, fontWeight: 500 }}>
                        {field.label}
                      </Typography>
                      {renderFieldInput(field)}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        );

      default:
        return 'Unknown step';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          minHeight: '600px',
        },
      }}
      BackdropProps={{
        style: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#344054' }}>
            Enhanced Bulk Edit Fiber Sites
          </Typography>
          {siteCodes.length > 0 && (
            <SiteCodesDisplay selectedSites={siteCodes} maxVisible={4} showCount={true} />
          )}
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={loading}
          sx={{
            color: '#667085',
            '&:hover': { backgroundColor: '#F5F5F5' },
            '&:disabled': { color: '#D0D5DD' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Loading Overlay */}
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              borderRadius: '8px',
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ color: '#BD1D23', mb: 1 }}>
                Updating Sites...
              </Typography>
              <Typography variant="body2" sx={{ color: '#667085' }}>
                Please wait while we update the selected sites
              </Typography>
            </Box>
          </Box>
        )}

        {/* Stepper */}
        <Box sx={{ mb: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Step Content */}
        {getStepContent(activeStep)}
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid #E0E0E0' }}>
        <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'flex-end' }}>
          {activeStep > 0 && (
            <Button
              onClick={handleBack}
              disabled={activeStep === 0}
              variant="outlined"
              sx={{
                color: '#667085',
                borderColor: '#D0D5DD',
                '&:hover': {
                  borderColor: '#98A2B3',
                  backgroundColor: '#F9FAFB',
                },
              }}
            >
              Back
            </Button>
          )}

          <Button
            onClick={activeStep === steps.length - 1 ? handleSubmit : handleNext}
            disabled={
              (activeStep === 0 && siteCodes.length === 0) ||
              (activeStep === 1 && selectedFields.length === 0) ||
              loading
            }
            variant="contained"
            sx={{
              backgroundColor: '#BD1D23',
              '&:hover': {
                backgroundColor: '#A0151A',
              },
              '&:disabled': {
                backgroundColor: '#D0D5DD',
                color: '#98A2B3',
              },
            }}
          >
            {activeStep === steps.length - 1
              ? loading
                ? 'Updating...'
                : 'Update Sites'
              : 'Continue'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EnhancedBulkEditModal;
